import { Component, Prop, h } from '@stencil/core';
import { getConstant } from '../../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const IMAGE_BASE = 'https://marketing-static.signnow.com/349/images/product/e-signature-v2/';
const IMAGE_BASE_V2 = 'https://marketing-static.signnow.com/345/images/product/e-signature-v2/';

/**
 * Features type 14 component - Colored Tiles Section
 * Displays four colored cards showcasing key e-signature product features in a 2x2 grid
 *
 * @product signnow
 * @version 1.0.0
 * @category SignNow
 * @contentConfig Colored tiles section with four feature cards for small teams and individuals
 * @example <sn-features-type-14 constants='{"FEATURES_TYPE_14_SUBTITLE":"Simplified document journeys for small teams and individuals","FEATURES_TYPE_14_CARD_1_TITLE":"eSign from anywhere","FEATURES_TYPE_14_CARD_1_DESCRIPTION":"Upload documents from your device or cloud and add your signature with ease: draw, upload, or type it on your mobile device or laptop.","FEATURES_TYPE_14_CARD_2_TITLE":"Prepare documents for sending","FEATURES_TYPE_14_CARD_2_DESCRIPTION":"Drag and drop fillable fields on your document and assign them to recipients. Reduce document errors and delight clients with an intuitive signing process.","FEATURES_TYPE_14_CARD_3_TITLE":"Secure signing is our priority","FEATURES_TYPE_14_CARD_3_DESCRIPTION":"Secure your documents by setting two-factor signer authentication. View who made changes and when in your document with the court-admissible Audit Trail.","FEATURES_TYPE_14_CARD_4_TITLE":"Collect signatures on the first try","FEATURES_TYPE_14_CARD_4_DESCRIPTION":"Define a signing order, configure reminders for signers, and set your document\'s expiration date. signNow will send you instant updates once your document is signed."}'></sn-features-type-14>
 * @constant FEATURES_TYPE_14_SUBTITLE - "Simplified document journeys for small teams and individuals" - Section subtitle
 * @constant FEATURES_TYPE_14_CARD_1_TITLE - "eSign from anywhere" - Card 1 title
 * @constant FEATURES_TYPE_14_CARD_1_DESCRIPTION - "Upload documents from your device or cloud and add your signature with ease: draw, upload, or type it on your mobile device or laptop." - Card 1 description
 * @constant FEATURES_TYPE_14_CARD_2_TITLE - "Prepare documents for sending" - Card 2 title
 * @constant FEATURES_TYPE_14_CARD_2_DESCRIPTION - "Drag and drop fillable fields on your document and assign them to recipients. Reduce document errors and delight clients with an intuitive signing process." - Card 2 description
 * @constant FEATURES_TYPE_14_CARD_3_TITLE - "Secure signing is our priority" - Card 3 title
 * @constant FEATURES_TYPE_14_CARD_3_DESCRIPTION - "Secure your documents by setting two-factor signer authentication. View who made changes and when in your document with the court-admissible Audit Trail." - Card 3 description
 * @constant FEATURES_TYPE_14_CARD_4_TITLE - "Collect signatures on the first try" - Card 4 title
 * @constant FEATURES_TYPE_14_CARD_4_DESCRIPTION - "Define a signing order, configure reminders for signers, and set your document's expiration date. signNow will send you instant updates once your document is signed." - Card 4 description
 */
@Component({
  tag: 'sn-features-type-14',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNFeaturesType14 {
  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * HTML tag for the heading element (h1, h2, h3, h4, h5, h6)
   * @default "h2"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h2';

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
  }

  private cards = [
    {
      id: '01',
      color: 'yellow',
      image: 'esign-wherever-you-are.svg',
      titleKey: 'FEATURES_TYPE_14_CARD_1_TITLE',
      titleDefault: 'eSign from anywhere',
      descriptionKey: 'FEATURES_TYPE_14_CARD_1_DESCRIPTION',
      descriptionDefault: 'Upload documents from your device or cloud and add your signature with ease: draw, upload, or type it on your mobile device or laptop.',
    },
    {
      id: '02',
      color: 'green',
      image: 'prepare-documents-for-sending.png',
      webpSrcSet: `${IMAGE_BASE_V2}prepare-documents-for-sending.webp 1x, ${IMAGE_BASE_V2}prepare-documents-for-sending@2x.webp 2x`,
      srcSet: `${IMAGE_BASE_V2}prepare-documents-for-sending.png 1x, ${IMAGE_BASE_V2}prepare-documents-for-sending@2x.png 2x`,
      titleKey: 'FEATURES_TYPE_14_CARD_2_TITLE',
      titleDefault: 'Prepare documents for sending',
      descriptionKey: 'FEATURES_TYPE_14_CARD_2_DESCRIPTION',
      descriptionDefault:
        'Drag and drop fillable fields on your document and assign them to recipients. Reduce document errors and delight clients with an intuitive signing process.',
    },
    {
      id: '03',
      color: 'violet',
      image: 'secure-signing-is-our-priority.svg',
      titleKey: 'FEATURES_TYPE_14_CARD_3_TITLE',
      titleDefault: 'Secure signing is our priority',
      descriptionKey: 'FEATURES_TYPE_14_CARD_3_DESCRIPTION',
      descriptionDefault:
        'Secure your documents by setting two-factor signer authentication. View who made changes and when in your document with the court-admissible Audit Trail.',
    },
    {
      id: '04',
      color: 'pink',
      image: 'collect-signatures-on-the-first-try.svg',
      titleKey: 'FEATURES_TYPE_14_CARD_4_TITLE',
      titleDefault: 'Collect signatures on the first try',
      descriptionKey: 'FEATURES_TYPE_14_CARD_4_DESCRIPTION',
      descriptionDefault:
        "Define a signing order, configure reminders for signers, and set your document's expiration date. signNow will send you instant updates once your document is signed.",
    },
  ];

  render() {
    return (
      <me-section class="features-type-14" yGap="2xl" yGapLg="5xl">
        <me-row direction="column">
          <me-column spanLg="12" class="features-type-14__heading-column">
            <me-heading variant="2" class="features-type-14__heading" tag={this.headingTag}>
              {getConstant(this.constants, 'FEATURES_TYPE_14_SUBTITLE', 'Simplified document journeys for small teams and individuals')}
            </me-heading>
          </me-column>
          <me-column spanLg="12" class="features-type-14__cards">
            {this.cards.map(card => (
              <div class={`features-type-14__card features-type-14__card--${card.color}`} key={card.id}>
                <div class="features-type-14__card-image">
                  <me-picture
                    src={card.srcSet ? undefined : `${IMAGE_BASE}${card.image}`}
                    srcSet={card.srcSet}
                    webpSrcSet={card.webpSrcSet}
                    alt=""
                    aspectRatio="auto"
                    lazy={true}
                  />
                </div>
                <div class="features-type-14__card-content">
                  <me-heading variant="4" class="features-type-14__card-title">
                    {getConstant(this.constants, card.titleKey, card.titleDefault)}
                  </me-heading>
                  <me-text class="features-type-14__card-description">{getConstant(this.constants, card.descriptionKey, card.descriptionDefault)}</me-text>
                </div>
              </div>
            ))}
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
